import { produce } from 'immer';
import { OmniElement, html, css, repeat, ref, createRef } from 'omni-ui';
import {
  constants as teamsConstants,
  MicrosoftAuthenticationHandler,
  AdaptiveCard,
} from 'omni-teams-sdk';
import 'omni-wysiwyg';

import { formatDate } from '../helpers/utils.js';

const {
  CATEGORY_OPTIONS,
  TEAMS_SEARCH_CHANNEL,
  TEAMS_SEARCH_CHAT,
  TEAMS_SEARCH_PEOPLE,
} = teamsConstants;

export class CampaignNotes extends OmniElement {
  /**
   * @property {Object} step - The active step detail object
   * @property {Object[]} notes
   * @property {{ fullName: string, personId: string }} notes[].author -  the user who created the note
   * @property {string} notes[].date - the date created
   * @property {Object} notes[].text - the note content
   * @property {{id: UUID, displayName: string}} notes[].taggedUsers - the Teams objects (users, chats, teams) who have been tagged in the note, a previous version of this module had { PersonGUID, Name }
   * @property {string} notes[].flatText - the note content flattened to be sent via teams SDK
   * @property {string} userInput
   */

  static get properties() {
    return {
      step: { type: Object },
      notes: { type: Array },
      env: { type: String },
      userInput: { state: true },
    };
  }

  static get styles() {
    return [
      ...super.styles,
      css`
        .omni .module-container {
          padding: 2.14rem 2.86rem 4.29rem 2.86rem;
        }
        .box-title {
          display: flex;
          justify-content: space-between;
          width: 20.357rem;
        }
        .box-title-text {
          font-size: 1.071rem;
        }
        .omni .modal-card-body {
          padding: 0;
          overflow: visible;
        }
        .omni .previous-notes h3.title {
          padding: 2.143rem 0 0.357rem;
          border-bottom: 0.071rem solid rgb(var(--rgb-border));
          margin: 0;
        }
        .omni .note-text {
          border-bottom: 0.071rem solid rgb(var(--rgb-border));
          padding: 0.5rem 0;
          margin: 0 0 0.5rem 0;
        }
        .omni label.note-label {
          padding: 0;
          margin-left: 0;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.notes = [];
    this.userInput = '';
    this.userInputFlatText = '';
    this.wysiwygRef = createRef();
    this.newMentionNotes = [];
  }

  connectedCallback() {
    super.connectedCallback();
    this.notes = this.savedData.notes ?? [];
    this.msAuth = new MicrosoftAuthenticationHandler();
  }

  /**
   * Initialize Microsoft auth handler
   */
  async firstUpdated() {
    await this.msAuth.init();
  }

  #dispatchModuleChange = () => {
    const { uuid_module: moduleId } = this.context;
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        moduleType: 'notes module',
        saveAction: async () => {
          const filteredTagNotes = this.notes.filter(
            note => note.newlyAdded && note.taggedUsers.length,
          );
          this.notes = produce(this.notes, draft => {
            draft.forEach(note => {
              // eslint-disable-next-line no-param-reassign
              delete note.newlyAdded;
            });
          });
          await this.updateComplete;
          await this.save({ notes: this.notes });
          if (filteredTagNotes.length) {
            this.#sendTeamsTag(filteredTagNotes);
          }
        },
      },
    });
  };

  /**
   * Method to send teams card to all tagged users.
   * @param {Array} taggedNotes - List of new notes that include tagged users
   */
  #sendTeamsTag = async taggedNotes => {
    const stageName = this.step.framework_template.parent.name;
    const stepName = this.step.framework_template.name;
    const stepOrder = this.step.framework_template.display_order_label;
    const {
      start_date: startDate,
      end_date: endDate,
      name: campaignName,
    } = this.campaign;
    const attachmentId = crypto.randomUUID();
    const adaptiveCardBuilder = new AdaptiveCard();

    const cardContent = async note => ({
      subject: null,
      body: {
        contentType: 'html',
        content: `<b>${AN?.sso?.user?.fullName}</b> mentioned you in: Campaign: </b>${campaignName} <b>Stage: </b> ${stageName} <b>Step: </b>${stepName} <br><br><attachment id="${attachmentId}"></attachment>`,
      },
      mentions: [],
      attachments: [
        {
          id: attachmentId,
          contentType: 'application/vnd.microsoft.card.adaptive',
          contentUrl: null,
          content: await adaptiveCardBuilder.buildCard({
            object_properties: {
              ...this.campaign,
              campaign_step_data: {
                step: `${stageName} | ${stepOrder}: ${stepName}`,
                duration: `${startDate} to ${endDate}`,
              },
              type: 'Campaign',
              url: window.location.href,
            },
            body_template: [
              {
                type: 'TextBlock',
                text: `${note.flatText}`,
                wrap: true,
              },
            ],
            footer_template: [
              {
                type: 'Container',
                items: [
                  {
                    type: 'ActionSet',
                    actions: [
                      {
                        type: 'Action.OpenUrl',
                        title: 'View',
                        url: `${window.location.href}`,
                      },
                    ],
                    horizontalAlignment: 'Center',
                  },
                ],
              },
            ],
          }),
          name: null,
          thumbnailUrl: null,
        },
      ],
    });

    // msAuth.sendCard never calls getToken -- selfId wil be undefined without.
    await this.msAuth.getToken();
    await Promise.allSettled(
      taggedNotes.map(async note => {
        await Promise.allSettled(
          note.taggedUsers.map(async ({ id, displayName, type, teamId }) => {
            if (!CATEGORY_OPTIONS.includes(type)) return Promise.resolve();

            const content = await cardContent(note);

            switch (type) {
              case TEAMS_SEARCH_PEOPLE:
                return this.msAuth.sendCard(content, { displayName, id, type });
              case TEAMS_SEARCH_CHAT:
                return this.msAuth.sendCardToChat(content, id);
              case TEAMS_SEARCH_CHANNEL:
                return this.msAuth.sendCardToChannel(content, teamId, id);
              default:
                return Promise.resolve();
            }
          }),
        );
      }),
    );
  };

  /**
   * Method to get list of mentioned users from a single note
   * @param {JSON Object} content - the returned JSON Object from WYSIWYG of user input
   * @return {Object []} - the mentioned users id and label from teams
   */
  #getMentions = content => {
    const mentions =
      content?.content?.reduce((agg, item) => {
        if (!item?.content?.length) return agg;

        // eslint-disable-next-line no-shadow
        const mentions = item?.content.filter(
          mention => mention.type?.toLowerCase() === 'mention',
        );

        if (mentions.length)
          mentions.forEach(mention => {
            // eslint-disable-next-line no-param-reassign
            agg[mention.attrs.id] = {
              id: mention.attrs.id,
              displayName: mention.attrs.label,
              type: mention.attrs.type,
              teamId: mention.attrs.teamId,
            };
          });

        return agg;
      }, {}) || {};

    return Object.values(mentions);
  };

  /**
   * Method to add note object to front of note arrary. Resets userInput text.
   */
  #addNote = () => {
    if (this.userInput) {
      this.notes = produce(this.notes, draft => {
        draft.unshift({
          author: {
            fullName: AN?.sso?.user?.fullName,
            personId: AN?.sso?.user?.personId,
          },
          date: new Date().toISOString(),
          text: this.userInput,
          taggedUsers: this.#getMentions(this.userInput),
          flatText: this.userInputFlatText,
          newlyAdded: true,
        });
      });
    }

    this.#dispatchModuleChange();
    this.#resetText();
  };

  #deleteNote = idx => {
    this.notes = produce(this.notes, draft => {
      draft.splice(idx, 1);
    });
    this.#dispatchModuleChange();
  };

  #resetText = () => {
    this.userInput = '';
    this.userInputFlatText = '';
    this.wysiwygRef.value.setContent('');
  };

  #renderPreviousNotes = () => {
    const { notes } = this;
    return html`
      <div class="previous-notes">
        ${notes.length === 0
          ? ''
          : html`
              <h3 class="title is-size-6 mt-5">Previous Comments</h3>
              ${repeat(
                this.notes,
                note => note.date,
                (note, idx) => html`
                  <div class="previous-note">
                    <omni-tooltip class="is-pulled-right mt-1">
                      <button
                        slot="invoker"
                        class="button is-text is-size-7 ${this.context.readonly
                          ? 'is-hidden'
                          : ''}"
                        @click=${() => {
                          this.#deleteNote(idx);
                        }}
                      >
                        <omni-icon
                          icon-id="omni:interactive:trash"
                          class="is-size-3"
                        ></omni-icon>
                      </button>
                      <div slot="content">Delete Comment</div>
                    </omni-tooltip>
                    <label class="note-label">
                      ${note.author?.fullName} | ${formatDate(note.date, true)}
                    </label>
                    <div>
                      <omni-wysiwyg
                        class="note-text"
                        .initialContent=${note.text}
                        display-only
                        overflow-visible
                        enable-mentions
                      ></omni-wysiwyg>
                    </div>
                  </div>
                `,
              )}
            `}
      </div>
    `;
  };

  render() {
    return html`
      <omni-style>
        <div class="box module-container">
          <div>
            <h2 class="title mb-5 is-3">${this.moduleTitle}</h2>
          </div>
          <section class="modal-card-body is-flex is-flex-direction-column">
            <label>${this.schema?.text_label}</label>
            <omni-wysiwyg
              ${ref(this.wysiwygRef)}
              placeholder=${this.schema?.placeholder ?? 'Enter your notes'}
              @change=${({ detail }) => {
                const { value, text } = detail;
                this.userInput = value;
                this.userInputFlatText = text;
              }}
              .initialContent=${this.userInput}
              .enabledExtensions=${['bold', 'italic', 'underline']}
              ?display-only=${this.context.readonly}
              enable-mentions
            ></omni-wysiwyg>
            ${this.#renderPreviousNotes()}
          </section>
          <footer class="modal-card-foot is-justify-content-right">
            <button
              class="button has-text-primary is-text"
              @click=${this.#resetText}
              ?disabled=${this.userInput.length === 0}
            >
              Cancel
            </button>
            <button
              class="button is-primary"
              @click=${this.#addNote}
              ?disabled=${!this.userInput}
            >
              Update
            </button>
          </footer>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-notes', CampaignNotes);
